import { createFileRoute, notFound, useRouter } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { getScanPayInfo } from '@/api/general';
import {
  countDownSecondsAtom,
  deadlineAtom,
  pollingIntervalIdAtom,
  servicesUrlAtom,
  statusAtom,
  transitionIdAtom,
} from '@/atoms/general';
import CardHeader from '@/components/card-header';
import ContactButton from '@/components/contact-button';
import HintSection from '@/components/hint-section';
import OrderNumberSection from '@/components/order-number-section';
import PageLoadingSpinner from '@/components/page-loading-spinner';
import TransferDetailScanPay from '@/components/transfer-detail-scan-pay';
import TransitionAmountSection from '@/components/transition-amount-section';
import { POLLING_INTERVAL, SCAN_PAY_DEADLINE } from '@/config';
import { SCAN_PAY } from '@/constants/general';
import { SCAN_PAY_STATUS } from '@/constants/status';

export const Route = createFileRoute('/_layout/scanPay/$id')({
  loader: async ({ params: { id } }) => {
    const paymentInfo = await getScanPayInfo(id);
    if (!paymentInfo) throw notFound();

    return paymentInfo;
  },
  pendingComponent: () => <PageLoadingSpinner />,
  component: ScanPay,
});

function ScanPay() {
  const paymentInfo = Route.useLoaderData();
  const { id } = Route.useParams();
  const router = useRouter();

  const [pollingIntervalId, setPollingIntervalId] = useAtom(pollingIntervalIdAtom);
  const setTransitionId = useSetAtom(transitionIdAtom);
  const setServicesUrl = useSetAtom(servicesUrlAtom);
  const setCountDownSeconds = useSetAtom(countDownSecondsAtom);
  const [status, setStatus] = useAtom(statusAtom);
  const [deadline, setDeadline] = useAtom(deadlineAtom);

  useEffect(() => {
    setTransitionId(id);
    setServicesUrl(paymentInfo.merchant_cs_url);

    // 保存當前路由，以便首頁可以重定向回來
    sessionStorage.setItem('lastPaymentRoute', `/scanPay/${id}`);
  }, [id, paymentInfo]);

  useEffect(() => {
    const deadline = dayjs(paymentInfo.created_time).add(SCAN_PAY_DEADLINE, 's');
    const calculatedCountDownSeconds = Math.floor((deadline.toDate().getTime() - new Date().getTime()) / 1000);

    setDeadline(deadline);
    setCountDownSeconds(calculatedCountDownSeconds);

    if (calculatedCountDownSeconds <= 0 && paymentInfo.web_status === SCAN_PAY_STATUS.PENDING) {
      setStatus(SCAN_PAY_STATUS.TIMEOUT);
    } else {
      setStatus(paymentInfo.web_status);
    }
  }, [id, paymentInfo]);

  useEffect(() => {
    if (pollingIntervalId) return;

    const intervalId = setInterval(() => {
      router.invalidate();
    }, POLLING_INTERVAL);

    setPollingIntervalId(intervalId);

    return () => {
      clearInterval(intervalId);
      setPollingIntervalId(null);
    };
  }, [router, setPollingIntervalId]);

  return (
    <main className="flex items-center justify-center main-height p-3 sm:p-6 md:p-10 bg-main-gray overflow-y-scroll">
      <div className="device-height w-[340px] bg-main-white rounded-xl shadow-custom relative flex flex-col">
        <ContactButton />
        <CardHeader />
        <div className="flex flex-col overflow-y-scroll flex-1">
          <div className="flex flex-col h-full">
            <OrderNumberSection orderId={paymentInfo?.merchant_order_id} createdTime={paymentInfo?.created_time} />
            <TransitionAmountSection amount={paymentInfo?.amount} />
            <TransferDetailScanPay
              qrContent={paymentInfo?.qr_content}
              status={status}
              deadline={deadline}
              name={paymentInfo?.merchant_member_name}
            />
            <HintSection type="scan-pay" />
          </div>
        </div>
      </div>
    </main>
  );
}
